import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, 
  FormControlLabel, Switch, Box, Grid, Typography, MenuItem, Divider, Alert, CircularProgress
} from '@mui/material';
import QuizEditor from './QuizEditor';
import api from '../utils/api';
import { FILE_UPLOAD_CONFIG } from '../utils/security';

interface ContentBlockFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void | Promise<void>;
  initialData?: any;
  sectionId: number;
  nextOrder?: number;
}

// Define the content structure for different block types
interface ContentData {
  // Text block
  text?: string;
  
  // Video block
  video_url?: string;
  video_title?: string;
  duration?: string;
  description?: string;
  
  // Link block
  link_url?: string;
  link_title?: string;
  requires_certification?: boolean;
  
  // Image block
  image_url?: string;
  storage_path?: string;
  caption?: string;
  
  // Quiz block
  quiz_title?: string;
  questions?: any[];
  passing_score?: number;
  
  // Test block
  test_title?: string;
}

const BLOCK_TYPES = [
  { value: 'text', label: 'Text Content' },
  { value: 'video', label: 'Video' },
  { value: 'link', label: 'External Link' },
  { value: 'image', label: 'Image' },
  { value: 'quiz', label: 'Quiz' },
  { value: 'test', label: 'Final Assessment' }
];

const ContentBlockForm: React.FC<ContentBlockFormProps> = ({ 
  open, onClose, onSubmit, initialData, sectionId, nextOrder = 1
}) => {
  const [form, setForm] = useState<{
    title: string;
    block_type: string;
    content: ContentData;
    order: number;
    is_required: boolean;
    passing_score?: number;
  }>({
    title: '',
    block_type: 'text',
    content: {},
    order: nextOrder,
    is_required: true,
    passing_score: 80
  });

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      return;
    }
    if (wasOpenRef.current) {
      return;
    }
    wasOpenRef.current = true;
    setImageFile(null);
    setSubmitError(null);

    if (initialData) {
      // Parse content if it's a JSON string
      let parsedContent = {};
      if (initialData.content) {
        if (typeof initialData.content === 'string') {
          try {
            parsedContent = JSON.parse(initialData.content);
          } catch (error) {
            console.error('Error parsing content JSON:', error);
            parsedContent = {};
          }
        } else {
          parsedContent = initialData.content;
        }
      }

      // For quiz blocks, copy questions from the questions array to content.questions
      // Map the API field names to the format expected by the quiz editor
      if ((initialData.block_type === 'quiz' || initialData.block_type === 'test')) {
        // Check both possible locations for questions
        const questionsSource = initialData.questions || initialData.content?.questions || [];
        console.log(`DEBUG: Loading ${initialData.block_type} block questions:`, {
          initialDataQuestions: initialData.questions,
          contentQuestions: initialData.content?.questions,
          questionsSource,
          questionsCount: questionsSource.length
        });
        
        if (questionsSource.length > 0) {
          (parsedContent as any).questions = questionsSource.map((q: any) => ({
            id: q.id || Math.random().toString(),
            question: q.question_text || q.question || '',
            options: q.options || [],
            correct_answer: q.correct_answer?.selected_option || q.correct_answer || 0,
            explanation: q.explanation || ''
          }));
          console.log(`DEBUG: Mapped ${questionsSource.length} questions for ${initialData.block_type} block`);
        } else {
          console.log(`DEBUG: No questions found for ${initialData.block_type} block`);
        }
      }

      setForm({
        title: initialData.title || '',
        block_type: initialData.block_type || 'text',
        content: parsedContent,
        order: initialData.order || 1,
        is_required: initialData.is_required !== false,
        passing_score: initialData.passing_score || 80
      });
    } else {
      setForm({
        title: '',
        block_type: 'text',
        content: {},
        order: nextOrder,
        is_required: true,
        passing_score: 80
      });
    }
  }, [initialData, open, nextOrder]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleContentChange = (field: string, value: any) => {
    setForm(prev => ({
      ...prev,
      content: {
        ...prev.content,
        [field]: value
      }
    }));
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: parseInt(value, 10) || 0
    }));
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    setSubmitting(true);

    try {
      let content = { ...form.content };

      if (form.block_type === 'image' && imageFile) {
        const formData = new FormData();
        formData.append('file', imageFile);
        const uploadResp = await api.post('/content-blocks/upload-image/', formData);
        content = {
          ...content,
          image_url: uploadResp.data.image_url,
          storage_path: uploadResp.data.storage_path,
        };
      }

      const payload = {
        ...form,
        content,
        section: sectionId,
        order: form.order ? parseInt(form.order.toString(), 10) : 1,
      };

      if (form.block_type === 'quiz' || form.block_type === 'test') {
        payload.content = {
          ...payload.content,
          quiz_title: form.title,
        };
      }

      if (form.block_type === 'test') {
        payload.passing_score = form.passing_score;
      }

      await onSubmit(payload);
    } catch (error: any) {
      const message =
        error.response?.data?.error ||
        (typeof error.response?.data === 'object'
          ? Object.values(error.response.data).flat().join(', ')
          : null) ||
        error.message ||
        'Failed to save content block';
      setSubmitError(String(message));
    } finally {
      setSubmitting(false);
    }
  };

  const isFormValid = () => {
    const basicValid = form.title.trim() !== '';
    
    if (!basicValid) return false;

    if (form.block_type === 'image') {
      const hasUrl = Boolean(form.content.image_url?.trim());
      const hasFile = Boolean(imageFile);
      if (!hasUrl && !hasFile) return false;
    }
    
    // Quiz-specific validation
    if (form.block_type === 'quiz' || form.block_type === 'test') {
      const quizContent = form.content;
      const hasQuestions = quizContent.questions && quizContent.questions.length > 0;
      const validPassingScore = !quizContent.passing_score || (quizContent.passing_score >= 0 && quizContent.passing_score <= 100);
      
      return hasQuestions && validPassingScore;
    }
    
    // Test-specific validation
    if (form.block_type === 'test') {
      const hasQuestions = form.content.questions && form.content.questions.length > 0;
      const validPassingScore = form.passing_score && form.passing_score >= 1 && form.passing_score <= 100;
      
      return hasQuestions && validPassingScore;
    }
    
    return true;
  };

  const renderContentFields = () => {
    switch (form.block_type) {
      case 'text':
        return (
          <TextField
            margin="dense"
            label="Text Content"
            multiline
            rows={6}
            fullWidth
            value={form.content.text || ''}
            onChange={(e) => handleContentChange('text', e.target.value)}
            placeholder="Enter your text content here..."
          />
        );

      case 'video':
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                label="Video URL (YouTube)"
                fullWidth
                value={form.content.video_url || ''}
                onChange={(e) => handleContentChange('video_url', e.target.value)}
                placeholder="https://www.youtube.com/watch?v=..."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                label="Video Title"
                fullWidth
                value={form.content.video_title || ''}
                onChange={(e) => handleContentChange('video_title', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                label="Description"
                multiline
                rows={3}
                fullWidth
                value={form.content.description || ''}
                onChange={(e) => handleContentChange('description', e.target.value)}
              />
            </Grid>
          </Grid>
        );

      case 'link':
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                label="Link URL"
                fullWidth
                value={form.content.link_url || ''}
                onChange={(e) => handleContentChange('link_url', e.target.value)}
                placeholder="https://..."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                label="Link Title"
                fullWidth
                value={form.content.link_title || ''}
                onChange={(e) => handleContentChange('link_title', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                label="Description"
                multiline
                rows={3}
                fullWidth
                value={form.content.description || ''}
                onChange={(e) => handleContentChange('description', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.content.requires_certification !== false}
                    onChange={(e) => handleContentChange('requires_certification', e.target.checked)}
                  />
                }
                label="Requires Review Certification"
              />
            </Grid>
          </Grid>
        );

      case 'image':
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Button variant="outlined" component="label" fullWidth>
                {imageFile ? imageFile.name : 'Upload image file'}
                <input
                  type="file"
                  hidden
                  accept={FILE_UPLOAD_CONFIG.allowedTypes.images.join(',')}
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    setImageFile(file);
                    if (file) {
                      setSubmitError(null);
                    }
                  }}
                />
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                JPEG, PNG, GIF, or WebP up to 5MB. Or paste an image URL below.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                label="Image URL (optional if uploading a file)"
                fullWidth
                value={form.content.image_url || ''}
                onChange={(e) => handleContentChange('image_url', e.target.value)}
                placeholder="https://..."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                label="Caption"
                fullWidth
                value={form.content.caption || ''}
                onChange={(e) => handleContentChange('caption', e.target.value)}
              />
            </Grid>
          </Grid>
        );

      case 'quiz':
        return (
          <Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  margin="dense"
                  label="Passing Score (%)"
                  type="number"
                  fullWidth
                  value={form.content.passing_score || ''}
                  onChange={(e) => handleContentChange('passing_score', parseInt(e.target.value) || 0)}
                  placeholder="e.g., 80"
                  inputProps={{ min: 0, max: 100 }}
                />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <QuizEditor
                  questions={form.content.questions || []}
                  onQuestionsChange={(questions) => handleContentChange('questions', questions)}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 'test':
        return (
          <Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  margin="dense"
                  label="Passing Score (%)"
                  type="number"
                  fullWidth
                  value={form.passing_score || 80}
                  onChange={(e) => setForm(prev => ({ ...prev, passing_score: parseInt(e.target.value) || 80 }))}
                  placeholder="e.g., 80"
                  inputProps={{ min: 1, max: 100 }}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <QuizEditor
                  questions={form.content.questions || []}
                  onQuestionsChange={(questions) => handleContentChange('questions', questions)}
                />
              </Grid>
            </Grid>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {initialData ? 'Edit Content Block' : 'Add New Content Block'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              margin="dense"
              label="Block Title"
              name="title"
              value={form.title}
              onChange={handleChange}
              fullWidth
              required
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              margin="dense"
              label="Content Type"
              name="block_type"
              value={form.block_type}
              onChange={handleChange}
              select
              fullWidth
              required
            >
              {BLOCK_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  name="is_required"
                  checked={form.is_required}
                  onChange={handleChange}
                />
              }
              label="Required Block"
            />
          </Grid>
          
          {form.block_type === 'test' && (
            <Grid item xs={12} md={6}>
              <TextField
                margin="dense"
                label="Passing Score (%)"
                type="number"
                value={form.passing_score || 80}
                onChange={(e) => setForm(prev => ({ ...prev, passing_score: parseInt(e.target.value) || 80 }))}
                fullWidth
                required
                inputProps={{ min: 1, max: 100 }}
              />
            </Grid>
          )}
          
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Content Configuration
            </Typography>
            {submitError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {submitError}
              </Alert>
            )}
            {renderContentFields()}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={!isFormValid() || submitting}
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : undefined}
        >
          {submitting ? 'Saving...' : initialData ? 'Save Changes' : 'Create Block'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContentBlockForm; 